'use client';

import { useMemo } from 'react';

import { cn } from '@kit/ui/utils';

/**
 * Renders AI-generated markdown content with proper formatting.
 *
 * Supports: **bold**, *italic*, `inline code`, ```code blocks```,
 * ## headings, - bullet lists, 1. numbered lists, and --- separators.
 *
 * Lightweight regex-based renderer — no external markdown library needed.
 */
export function AiMessageContent({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  const rendered = useMemo(() => parseMarkdown(content), [content]);

  return (
    <div
      className={cn(
        'ai-message-content max-w-none text-sm break-words',
        // Headings
        '[&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-sm [&_h2]:font-semibold',
        '[&_h3]:mt-2 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold',
        // Paragraphs
        '[&_p]:my-1 [&_p]:leading-relaxed',
        // Lists
        '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4',
        '[&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-4',
        '[&_li]:my-0.5',
        // Inline code
        '[&_code]:rounded [&_code]:bg-black/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs dark:[&_code]:bg-white/10',
        // Code blocks
        '[&_pre]:my-2 [&_pre]:rounded-lg [&_pre]:bg-black/10 [&_pre]:p-3 [&_pre]:text-xs dark:[&_pre]:bg-white/10',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
        // Horizontal rule
        '[&_hr]:my-2 [&_hr]:border-current/20',
        // Strong/em
        '[&_strong]:font-semibold',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
}

/**
 * Simple markdown-to-HTML parser for AI chat messages.
 * Covers the subset of markdown that AI models typically produce.
 */
function parseMarkdown(text: string): string {
  // Escape HTML entities first to prevent XSS
  let html = escapeHtml(text);

  // Code blocks (```...```) — must be before inline processing
  html = html.replace(
    /```(\w*)\n([\s\S]*?)```/g,
    (_match, _lang, code) => `<pre><code>${code.trimEnd()}</code></pre>`,
  );

  // Process line by line for block elements
  const lines = html.split('\n');
  const result: string[] = [];
  let inList: 'ul' | 'ol' | null = null;
  let inParagraph = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const trimmed = line.trim();

    // Skip empty lines — close any open blocks
    if (!trimmed) {
      if (inList) {
        result.push(inList === 'ul' ? '</ul>' : '</ol>');
        inList = null;
      }
      if (inParagraph) {
        result.push('</p>');
        inParagraph = false;
      }
      continue;
    }

    // Preserve pre blocks as-is
    if (
      trimmed.startsWith('<pre>') ||
      result[result.length - 1]?.includes('<pre>')
    ) {
      result.push(line);
      continue;
    }

    // Headings
    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)/);
    if (headingMatch) {
      closeBlocks();
      const level = headingMatch[1]!.length + 1; // h2, h3, h4
      result.push(`<h${level}>${applyInline(headingMatch[2]!)}</h${level}>`);
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(trimmed)) {
      closeBlocks();
      result.push('<hr/>');
      continue;
    }

    // Bullet list items
    const bulletMatch = trimmed.match(/^[-*]\s+(.+)/);
    if (bulletMatch) {
      if (inParagraph) {
        result.push('</p>');
        inParagraph = false;
      }
      if (inList !== 'ul') {
        if (inList === 'ol') result.push('</ol>');
        result.push('<ul>');
        inList = 'ul';
      }
      result.push(`<li>${applyInline(bulletMatch[1]!)}</li>`);
      continue;
    }

    // Numbered list items
    const numberedMatch = trimmed.match(/^\d+\.\s+(.+)/);
    if (numberedMatch) {
      if (inParagraph) {
        result.push('</p>');
        inParagraph = false;
      }
      if (inList !== 'ol') {
        if (inList === 'ul') result.push('</ul>');
        result.push('<ol>');
        inList = 'ol';
      }
      result.push(`<li>${applyInline(numberedMatch[1]!)}</li>`);
      continue;
    }

    // Regular text — wrap in paragraph
    if (inList) {
      result.push(inList === 'ul' ? '</ul>' : '</ol>');
      inList = null;
    }
    if (!inParagraph) {
      result.push('<p>');
      inParagraph = true;
    } else {
      result.push('<br/>');
    }
    result.push(applyInline(trimmed));
  }

  // Close any remaining open blocks
  if (inList) result.push(inList === 'ul' ? '</ul>' : '</ol>');
  if (inParagraph) result.push('</p>');

  return result.join('\n');

  function closeBlocks() {
    if (inList) {
      result.push(inList === 'ul' ? '</ul>' : '</ol>');
      inList = null;
    }
    if (inParagraph) {
      result.push('</p>');
      inParagraph = false;
    }
  }
}

/** Apply inline markdown: bold, italic, inline code */
function applyInline(text: string): string {
  return (
    text
      // Inline code (must be before bold/italic to avoid conflicts)
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
  );
}

/** Escape HTML to prevent XSS */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
